import { Fragment, useLayoutEffect, useRef, useState } from 'react'
import { Fish } from 'lucide-react'
import type { SectionColor } from '@shared/types'
import { COLOR_GRADES } from '@shared/colorGrades'
import { outputPresetIdFor } from '@shared/outputPresets'
import { useEditorStore } from '../../../store/editorStore'
import { useUiStore } from '../../../store/uiStore'
import { EmptyState } from '../../common/States'
import { activeOverlaysAt } from '../../../lib/timelineMath'
import { usePreviewPlayback } from './usePreviewPlayback'
import { usePreviewAudio } from './usePreviewAudio'
import { OverlayLayer } from './OverlayLayer'
import { PlaybackBar } from './PlaybackBar'

/**
 * reviewer 6/10 M4: fits a box of the given `aspectW:aspectH` ratio into an
 * area of size (w,h), preferring to fill it exactly on whichever axis is the
 * tighter constraint — same shape as the original task-spec formula
 * (`size = min(w, h*9/16) -> width; size*16/9 -> height`), generalized so a
 * dest=x (4:5, 1080x1350) Composition's studio preview actually shows 4:5
 * instead of a hardcoded 9:16 box. `composition.output` already drove
 * OverlayLayer's own coordinate math below — this was the one remaining
 * place that assumed 9:16 outright.
 */
function fitToAspect(w: number, h: number, aspectW: number, aspectH: number): { width: number; height: number } {
  if (w <= 0 || h <= 0 || aspectW <= 0 || aspectH <= 0) return { width: 0, height: 0 }
  const width = Math.min(w, (h * aspectW) / aspectH)
  return { width, height: (width * aspectH) / aspectW }
}

export function PreviewPanel() {
  const composition = useEditorStore((s) => s.composition)
  const currentSec = useEditorStore((s) => s.playback.currentSec)
  const { videoRefs, bgVideoRefs, activeSlot, activeSegment, totalSec } = usePreviewPlayback(composition)
  const { audioClips, setAudioRef, onLoadedMetadataFor } = usePreviewAudio(composition)
  // P10-2 担当B §3 item 5 (composition.sourceAudio「元音声プレビュー」): active
  // な foreground <video> の muted はここ（JSX の宣言的な prop）で切替える —
  // React は muted を「値が変わった時だけプロパティごと再設定する」特別扱いを
  // しているので、この bool を計算式にするだけで安全に効く（usePreviewAudio.ts
  // の <audio> 側が同じ previewMuted を購読して el.muted を effect 内で直接
  // 触っているのとは違う経路だが、結果は同じ「第1段のミュート方針に従う」）。
  // volume（React は音量プロパティを宣言的に扱えない既知の制約）は
  // usePreviewPlayback.ts の effect が activeVideo.volume に直接書き込む。
  const previewMuted = useUiStore((s) => s.previewMuted)
  const sourceAudioEnabled = composition?.sourceAudio?.enabled ?? false

  // The frame must always be exactly 9:16 regardless of window size or
  // Timeline height. `areaRef` below is `flex-1 min-h-0`, so it always
  // occupies whatever vertical space is left after the PlaybackBar's own
  // natural height — a ResizeObserver on it reports the true available
  // (W,H) for any cause of a resize (window resize, Timeline resize,
  // sidebar resize) without special-casing any of them. Plain CSS
  // `aspect-ratio` on the frame itself (the previous approach) breaks under
  // exactly this layout: a flex item's aspect-ratio-derived main-axis size
  // gets shrunk independently by the flexbox algorithm once the column runs
  // out of height, so the CSS-set width stays fixed while only the height
  // shrinks — producing the squarish ~530x615 box from the bug report
  // instead of a true 9:16 frame. Measuring the real box and setting both
  // dimensions explicitly sidesteps that.
  const areaRef = useRef<HTMLDivElement>(null)
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 })
  // reviewer 6/10 M4: default (no Composition loaded yet) stays 1080x1920 —
  // byte-identical fallback to the previous hardcoded 9:16 box.
  const outputWidth = composition?.output.width ?? 1080
  const outputHeight = composition?.output.height ?? 1920
  // P10-3 (2026-09-06, 担当B「出力サイズ切替」): TabExport のプリセット select
  // と同じ判定 (shared/src/outputPresets.ts) — 'custom' は手編集JSON/旧サイズ
  // で、4プリセットのどれとも width/height が一致しない場合。
  const outputPresetId = outputPresetIdFor({ width: outputWidth, height: outputHeight })
  const aspectBadgeLabel = outputPresetId === 'custom' ? `${outputWidth}×${outputHeight}` : outputPresetId

  useLayoutEffect(() => {
    const el = areaRef.current
    if (!el) return
    const compute = () => {
      const rect = el.getBoundingClientRect()
      setFrameSize(fitToAspect(rect.width, rect.height, outputWidth, outputHeight))
    }
    compute()
    const ro = new ResizeObserver(compute)
    ro.observe(el)
    return () => ro.disconnect()
  }, [outputWidth, outputHeight])

  const sectionColor: SectionColor =
    composition?.sections.find((s) => s.id === activeSegment?.sectionId)?.color ?? 'none'
  const grade = COLOR_GRADES[sectionColor]
  const cssFilter = grade?.css || 'none'
  // REVIEW_v1.md M2/M3: the ffmpeg render's actual per-section color now
  // comes from compositing a flat `swatch` color on top with a
  // luma-gated overlay (shared/src/colorGrades.ts) — a CSS `filter` alone
  // cannot express that same split/drawbox/alphamerge/overlay graph, so the
  // preview approximates it with a real color layer on top of the video,
  // `mix-blend-mode: soft-light` (a genuine blend against the video pixels
  // beneath, unlike the ffmpeg fragment's luma-gated overlay — an
  // approximation, not a pixel-identical preview, but it moves the same
  // direction: no layer at all for 'none'/'white' (white has no swatch
  // blend in the render either — see colorGrades.ts).
  const showColorLayer = sectionColor !== 'none' && sectionColor !== 'white' && Boolean(grade?.swatch)

  const activeOverlays = composition ? activeOverlaysAt(composition.tracks.overlays, currentSec) : []
  const hasSegments = (composition?.tracks.video.length ?? 0) > 0

  // Segment.fit (shared/types.ts) drives how the active video fills the
  // frame, mirroring the server render (server/src/render/ffmpegArgs.ts
  // buildSegmentFilterGraph): 'cover' scales+crops to fill exactly, anchored
  // horizontally by cropX (0=left..1=right, vertical always centered — same
  // contract as ffmpegArgs.ts's computeCoverCrop, which `object-position:
  // ${cropX*100}% 50%` reproduces 1:1 for a CSS `object-fit: cover` box);
  // 'contain' letterboxes the full frame centered, with a blurred+enlarged
  // copy of the same video filling the background (approximates the
  // render's `boxblur=20:5` cover-cropped background — not pixel-identical,
  // same "approximation, not exact" spirit as the color-grade layer above).
  const fit = activeSegment?.fit ?? 'cover'
  const cropX = activeSegment?.cropX ?? 0.5
  const isContain = fit === 'contain'

  // Fallback (pre-first-measurement / measurement failed): a plain
  // aspect-ratio box so nothing collapses to 0x0 before the
  // ResizeObserver's first callback fires.
  const frameStyle = (
    frameSize.width > 0 && frameSize.height > 0
      ? { width: frameSize.width, height: frameSize.height, containerType: 'size' }
      : { width: '100%', maxWidth: 380, aspectRatio: `${outputWidth}/${outputHeight}`, containerType: 'size' }
  ) as React.CSSProperties
  const barWidth = frameSize.width > 0 ? frameSize.width : undefined

  return (
    <div className="flex-1 flex flex-col items-center bg-ink-bg px-6 py-4 min-w-0 min-h-0">
      {/* P10-1 担当C: composition.tracks.audio の各 clip 用 hidden <audio>。
          usePreviewAudio.ts が volume/currentTime/play/pause を同期する
          （Web Audio API は使わない — 別オリジンの MediaElementSource は
          無音になるため）。視覚的には何も描画しない。 */}
      {audioClips.map((clip) => (
        <audio
          key={clip.id}
          ref={setAudioRef(clip.id)}
          onLoadedMetadata={onLoadedMetadataFor(clip.id)}
          preload="auto"
          hidden
          data-testid={`preview-audio-${clip.id}`}
        />
      ))}
      <div ref={areaRef} className="flex-1 min-h-0 w-full flex items-center justify-center">
        <div
          className="relative rounded-lg overflow-hidden bg-black border border-ink-border shadow-glass"
          style={frameStyle}
          data-testid="preview-frame"
        >
          {/* P10-3 (2026-09-06, 担当B「出力サイズ切替」): 出力プリセットのバッジ。
              空タイムライン時も出す(サイズ切替はセグメント追加前から可能なので)。 */}
          <div
            data-testid="preview-aspect-badge"
            className="absolute top-2 right-2 z-20 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white/85 pointer-events-none"
          >
            {aspectBadgeLabel}
          </div>
          {!hasSegments ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <EmptyState
                icon={<Fish size={28} />}
                title="タイムラインが空です"
                description="左の素材ビンからシーンをドラッグして追加してください。"
              />
            </div>
          ) : (
            <>
              {[0, 1].map((slot) => {
                const isActive = activeSlot === slot
                return (
                  <Fragment key={slot}>
                    <video
                      ref={bgVideoRefs[slot as 0 | 1]}
                      muted
                      playsInline
                      aria-hidden
                      data-testid={`preview-video-bg-${slot}`}
                      className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                      style={{
                        opacity: isActive && isContain ? 1 : 0,
                        filter: 'blur(24px)',
                        transform: 'scale(1.15)',
                      }}
                    />
                    <video
                      ref={videoRefs[slot as 0 | 1]}
                      // P10-2 担当B §3 item 5: only the ACTIVE slot may ever be
                      // unmuted, and only when sourceAudio is enabled and the
                      // global preview mute isn't on — the standby slot (and
                      // both bg slots above) stay hardcoded `muted` always.
                      muted={!(isActive && sourceAudioEnabled && !previewMuted)}
                      playsInline
                      className={`absolute inset-0 w-full h-full transition-opacity duration-75 ${
                        isActive && isContain ? 'object-contain' : 'object-cover'
                      }`}
                      style={{
                        opacity: isActive ? 1 : 0,
                        filter: isActive ? cssFilter : undefined,
                        objectPosition: isActive && !isContain ? `${cropX * 100}% 50%` : undefined,
                      }}
                      data-testid={`preview-video-${slot}`}
                      data-active={isActive}
                    />
                  </Fragment>
                )
              })}
              {showColorLayer && (
                <div
                  aria-hidden
                  data-testid="section-color-layer"
                  className="absolute inset-0 pointer-events-none"
                  style={{ backgroundColor: grade!.swatch, mixBlendMode: 'soft-light', opacity: 0.35 }}
                />
              )}
              <OverlayLayer
                overlays={activeOverlays}
                output={composition?.output ?? { width: 1080, height: 1920, fps: 30 }}
              />
            </>
          )}
        </div>
      </div>
      <div
        className="w-full"
        style={barWidth ? { width: barWidth, maxWidth: barWidth } : { maxWidth: 380 }}
      >
        <PlaybackBar totalSec={totalSec} />
      </div>
    </div>
  )
}
